import { ChangeDetectionStrategy, Component, Inject, inject, OnDestroy, OnInit, ElementRef } from '@angular/core';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { UseType } from 'go-modules/models/use-type/use-type.interface';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Language, NgxAiPromptService } from '@go-modules/services/ai-prompt/ai-prompt.service';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { SignUpService } from '@go-modules/services/sign-up/sign-up.service';
import {
	BehaviorSubject,
	Observable,
	combineLatest,
	forkJoin,
	catchError,
	EMPTY,
	Subject,
	of
} from 'rxjs';
import { filter, map, shareReplay, switchMap, takeUntil, tap, take } from 'rxjs/operators';
import { TeaseWallConfig } from '@go-modules/directives/tease-wall/tease-wall.config';
import { SelfPayService } from '@go-modules/services/self-pay/self-pay.service';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import {
	TEASE_WALL_BETA_REQUEST_REMEMBER_KEY,
	TEASE_WALL_UPGRADE_REMEMBER_KEY
} from '@go-modules/directives/tease-wall/constants';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { NgxLicenseUpgradeService } from '@go-modules/services/license/license-upgrade/license-upgrade.service';
import {
	ActivityEditorAiMarkersTour,
	activityEditorAiMarkersTourToken
} from 'go-modules/activity-editor/activity-editor-ai-markers-tour/activity-editor-ai-markers-tour.service';
import { TranslateService } from '@ngx-translate/core';
import { Activity } from '@go-modules/interfaces/activity';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { CourseTrialService } from '@go-modules/services/course-trial/course-trial.service';
import type { CourseFreeTrialResult } from '@go-modules/services/course-trial/course-trial.service';
import type { LlmDefinition } from '@go-modules/interfaces/llm-definition';
import { BaseMarker, TagCategory } from '@go-modules/interfaces/markers/marker';
import * as Color from 'color';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';

export const AI_MARKERS_SELECTED_USE_TYPE_ID = 'ai-markers-selected-use-type';

export type PromptItem = LlmDefinition;

export enum DataSourceType {
	TEASED = 'teased',
	REAL = 'real'
}

export interface AiMarkersDialogData {
	activity: Activity;
	llmDefinitions: LlmDefinition[];
	aiTranscriptionLanguage: string | null;
}

@Component({
	selector: 'ai-markers-dialog',
	templateUrl: './ai-markers-dialog.component.html',
	styleUrls: ['./ai-markers-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
	host: {
		'[class]': 'responsiveLayout.cssClassString()'
	}
})
export class AIMarkersDialogComponent implements OnInit, OnDestroy {
	public dialogTitleId = `ai-markers-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	protected responsiveLayout = inject(ResponsiveLayoutService);
	public checkedIds$ = new BehaviorSubject<Set<number>>(new Set());
	public selectedCount$ = this.checkedIds$.pipe(
		map((set) => set.size)
	);

	public loading$    = new BehaviorSubject<boolean>(true);
	public useTypes$   = new BehaviorSubject<UseType[]>([]);
	public languages$  = new BehaviorSubject<Language[]>([]);
	public showAi$     = new BehaviorSubject<boolean>(true);
	public useTypeFilter$   = new BehaviorSubject<number>(0);
	public selectedLanguage$ = new BehaviorSubject<Language>(null);
	public expandedCategories$ = new BehaviorSubject<Set<number>>(new Set());

	public aiLanguageFlag = false;
	public aiConfigFlag = false;
	public shouldTease = false;
	public readonly AiMarkersLimit = clientSettings.AiMarkersLimit;
	public readonly UseTypeAll: UseType = {
		use_type_id: 0, name: 'All', active: 1, category: '', slug: ''
	};

	private defaultUseTypeId = 0;
	private shouldAutoSelectMarkers = true;
	private destroy$ = new Subject<void>();

	public aiMarkerTeaseConfig: TeaseWallConfig = {
		useRealData: true,
		translationKey: 'tease-wall_request-button',
		rememberKey: TEASE_WALL_BETA_REQUEST_REMEMBER_KEY,
		select: []
	};

	public toggleTeaseConfig: TeaseWallConfig = {
		useRealData: true,
		translationKey: '',
		rememberKey: null,
		select: [
			'.ai-comments-toggle'
		]
	};

	public checkboxTeaseConfig: TeaseWallConfig = {
		useRealData: true,
		translationKey: '',
		rememberKey: null,
		select: [
			'.checkbox-to-blur'
		]
	};

	private dataSource$ = new BehaviorSubject<DataSourceType | null>(null);
	public rawPrompts$: Observable<PromptItem[]> = this.dataSource$.pipe(
		filter((source) => source !== null),
		switchMap((source) =>
			source === DataSourceType.TEASED
				? this.getTeasedPrompts()
				: this.getRealPrompts()
		),
		shareReplay(1)
	);

	public filteredPrompts$: Observable<PromptItem[]> = combineLatest([
		this.rawPrompts$,
		this.useTypeFilter$,
		this.showAi$
	]).pipe(
		map(([prompts, useTypeId, show]) =>
			!show ? []
				: (useTypeId === 0
					? prompts
					: prompts.filter((p) =>
						p.use_types.some((u) => u.use_type_id === useTypeId)
					)
				)
		),
		tap((filtered) => {
			// Auto-select
			if (this.shouldAutoSelectMarkers && this.checkedIds$.value.size === 0) {
				const defaults = new Set<number>(
					filtered
						.filter((p) => p.use_types.some((u) =>
							u.use_type_id === this.defaultUseTypeId && u.is_default))
						.map((p) => p.id)
				);
				this.checkedIds$.next(defaults);
			}
		})
	);

	public uniqueCategories$ = this.filteredPrompts$.pipe(
		map((prompts) => {
			if (!this.aiConfigFlag) {
				return [];
			} else {
				const seen = new Set<number>();
				return prompts
					.map((p) => p.tags?.[0]?.category)
					.filter((c) => {
						if (!c || seen.has(c.id)) return false;
						seen.add(c.id);
						return true;
					})
					.sort((a, b) => a.id - b.id);
			}
		}),
		shareReplay(1)
	);

	constructor (
		public dialogRef: GoDialogRef,
		public featureFlagService: NgxFeatureFlagService,
		@Inject(GO_MODAL_DATA) public data: AiMarkersDialogData,
		private signUpService: SignUpService,
		private aiPromptService: NgxAiPromptService,
		private selfPayService: SelfPayService,
		private ngxGoToastService: NgxGoToastService,
		private ngxLicenseUpgradeService: NgxLicenseUpgradeService,
		private translate: TranslateService,
		private ngxActivityService: NgxActivityService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(activityEditorAiMarkersTourToken) private activityEditorAiMarkersTour: ActivityEditorAiMarkersTour,
		@Inject('Window') private window: Window,
		private courseTrialService: CourseTrialService,
		private elementRef: ElementRef
	) {
	}

	public ngOnInit (): void {
		this.aiLanguageFlag = this.featureFlagService.isAvailable('AI_LANGUAGE');
		this.aiConfigFlag = this.featureFlagService.isAvailable('AI_CONFIG');

		// chicken or the egg:
		// subscribing to filteredPrompts sets "loading$" to false
		// but we don't subscribe to filteredPrompts in the template until "loading$" is true, making it load infinitely
		// subscribe here instead to always trigger this data flow on init
		this.filteredPrompts$.pipe(takeUntil(this.destroy$)).subscribe();

		this.ngxActivityService.getDefault()
			.subscribe((defaultActivity) => {
				this.defaultUseTypeId = defaultActivity.aiUseType?.use_type_id;
				if (!this.defaultUseTypeId) {
					this.defaultUseTypeId = this.selectedService.getAccount()?.use_type_id ?? 0;
					this.shouldAutoSelectMarkers = false;
				}
				this.shouldTease = !this.selectedService.getLicense()?.salesforce_license.ai_prompts_enabled;
				this.dataSource$.next(this.shouldTease ? DataSourceType.TEASED : DataSourceType.REAL);

				// End the "activity editor ai marker tour" when user reaches here to prevent showing it again
				// Currently it is set to show 10 times
				if (this.activityEditorAiMarkersTour.tour) {
					const tour = this.activityEditorAiMarkersTour.tour;
					this.activityEditorAiMarkersTour.goTour.markTourViewed(
						tour.getConfig().viewTrackKey,
						tour.getConfig().constraints.maxTourViews
					);
				}
			});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public selectPrompt (checked: boolean, prompt: PromptItem): void {
		this.shouldAutoSelectMarkers = false;
		const set = new Set(this.checkedIds$.value);
		if (checked) {
			set.add(prompt.id);
		} else {
			set.delete(prompt.id);
		}
		this.checkedIds$.next(set);
	}

	public getCategoryColor (color: string): string {
		return Color('#' + color).darken(0.51).hex();
	}

	public cancel () {
		this.dialogRef.close(null);
	}

	public save () {
		combineLatest([
			this.rawPrompts$,
			this.checkedIds$,
			this.selectedLanguage$
		]).pipe(
			takeUntil(this.destroy$),
			take(1)
		).subscribe(([filtered, ids, lang]) => {
			const selected = filtered.filter((p) => ids.has(p.id));
			if (selected.length > this.AiMarkersLimit) return;

			const key = `${this.userService.currentUser.user_id}-${AI_MARKERS_SELECTED_USE_TYPE_ID}`;
			this.window.localStorage.setItem(key, String(this.useTypeFilter$.value));

			const payload = { ai_transcription_language: lang.language_code, llm_definitions: selected };
			this.dialogRef.close(payload);
		});
	}

	public toggleCategory (category: TagCategory) {
		const currentExpanded = new Set(this.expandedCategories$.value);
		if (currentExpanded.has(category.id)) {
			currentExpanded.delete(category.id);
		} else {
			currentExpanded.add(category.id);
		}
		this.expandedCategories$.next(currentExpanded);
	}

	public handleCategoryHeaderKeydown (event: KeyboardEvent, category: TagCategory): void {
		// Let nested interactive elements (e.g., Deselect All button) handle their own keyboard events.
		if (event.target !== event.currentTarget) {
			return;
		}

		if (event.repeat) {
			return;
		}

		if (!this.isKeyboardActivationKey(event)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.toggleCategory(category);
	}

	public countByCategory$ (category: TagCategory): Observable<number> {
		return combineLatest([
			this.filteredPrompts$,
			this.checkedIds$
		]).pipe(
			map(([prompts, checkedIds]) =>
				prompts.filter((p) =>
					checkedIds.has(p.id) && p.tags[0].category?.id === category.id
				).length
			)
		);
	}

	public getPromptsByCategory (category: TagCategory): Observable<LlmDefinition[]> {
		return this.filteredPrompts$.pipe(
			map((prompts) => prompts.filter((p) => p.tags[0].category?.id === category.id))
		);
	}

	public isAllSelectedInCategory (category: TagCategory): Observable<boolean> {
		return combineLatest([
			this.filteredPrompts$,
			this.checkedIds$
		]).pipe(
			map(([prompts, checkedIds]) => {
				const categoryPrompts = prompts.filter((p) => p.tags[0].category?.id === category.id);
				return categoryPrompts.length > 0 && categoryPrompts.every((p) => checkedIds.has(p.id));
			})
		);
	}

	public selectCategoryPrompts (event: Event, category: TagCategory) {
		this.shouldAutoSelectMarkers = false;
		event.stopPropagation();

		combineLatest([
			this.filteredPrompts$,
			this.checkedIds$
		]).pipe(
			take(1),
			map(([prompts, checkedIds]) => {
				const set = new Set(checkedIds);
				const categoryPrompts = prompts.filter((p) => p.tags[0].category?.id === category.id);
				const isAllSelected = categoryPrompts.length > 0 && categoryPrompts.every((p) => checkedIds.has(p.id));

				if (isAllSelected) {
					// Deselect all prompts in this category
					categoryPrompts.forEach((p) => set.delete(p.id));
				} else {
					// Select all prompts in this category
					categoryPrompts.forEach((p) => set.add(p.id));
				}

				return set;
			})
		).subscribe((newCheckedIds) => {
			this.checkedIds$.next(newCheckedIds);
		});
	}

	public handleSelectCategoryPromptsKeydown (event: KeyboardEvent): void {
		if (!event.repeat || !this.isKeyboardActivationKey(event)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
	}

	public handlePromptCheckboxKeydown (event: KeyboardEvent): void {
		if (!this.isEnterKey(event)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
	}

	private isKeyboardActivationKey (event: KeyboardEvent): boolean {
		return this.isEnterKey(event) || this.isSpaceKey(event);
	}

	private isEnterKey (event: KeyboardEvent): boolean {
		return ['Enter', 'NumpadEnter'].includes(event.key)
			|| ['Enter', 'NumpadEnter'].includes(event.code);
	}

	private isSpaceKey (event: KeyboardEvent): boolean {
		return [' ', 'Space', 'Spacebar'].includes(event.key) || event.code === 'Space';
	}

	public toggleAiMarkers (e: MatSlideToggleChange) {
		this.showAi$.next(e.checked);
		if (!e.checked) this.checkedIds$.next(new Set());
	}

	public selectedCountAriaLabel (count: number | null): string {
		return `${count ?? 0} ${
			this.translate.instant('activity-editor-new_ai-markers-modal-selected-count-discipline')
		}`;
	}

	public categoryCountAriaLabel (count: number | null): string {
		return `${count ?? 0} ${
			this.translate.instant('activity-editor-new_ai-markers-modal-selected-count-category')
		}`;
	}

	public markerCheckboxAriaLabel (prompt: LlmDefinition): string {
		return prompt.tags[0].tag_name;
	}

	public markerAriaLabel (prompt: LlmDefinition): string {
		return this.markerCheckboxAriaLabel(prompt);
	}

	public promptDescriptionButtonAriaLabel (prompt: LlmDefinition): string {
		return this.translate.instant('activity-editor-new_ai-markers-modal-description-help_aria-label', {
			markerName: prompt.tags[0].tag_name
		});
	}

	public markerInput (prompt: LlmDefinition): BaseMarker {
		return prompt.tags[0];
	}

	public handleSaveClick (): void {
		if (this.shouldTease) {
			const teaseWallButton = this.elementRef.nativeElement.querySelector('tease-wall .primary-btn');
			if (teaseWallButton) {
				teaseWallButton.click();
			}
		} else {
			this.save();
		}
	}

	private fetchDataOnInit (): Observable<{
		useTypes: UseType[],
		raw: PromptItem[],
		languages: Language[]
	}> {
		return forkJoin({
			useTypes: this.signUpService.getUseTypes(false, true).pipe(
				map((u) => [this.UseTypeAll, ...u])
			),
			raw: this.selectedService.getGroup()
				? this.aiPromptService.getLlmDefinitions(this.selectedService.getGroup())
				: of([]),
			languages: this.aiPromptService.languages()
		});
	}

	private getTeasedPrompts (): Observable<PromptItem[]> {
		this.shouldAutoSelectMarkers = false;
		this.loading$.next(true);
		this.checkedIds$.next(new Set());
		this.setupTeaseConfig();

		return this.fetchDataOnInit().pipe(
			tap(({ useTypes, languages }) => {
				this.useTypes$.next(useTypes);
				if (useTypes.length > 0) {
					this.useTypeFilter$.next(useTypes[0].use_type_id);
				}
				this.languages$.next([
					{ language_code: null, language_name: this.translate.instant('common_auto') },
					...languages
				]);
				this.selectedLanguage$.next(languages[0]);
				this.loading$.next(false);
			}),
			map(({ raw }) => raw)
		);
	}

	private getRealPrompts (): Observable<PromptItem[]> {
		this.loading$.next(true);
		this.checkedIds$.next(new Set());
		this.setupRealConfig();

		return this.fetchDataOnInit().pipe(
			tap(({ useTypes, languages }) => {
				this.useTypes$.next(useTypes);

				const stored = this.window.localStorage.getItem(
					`${this.userService.currentUser.user_id}-${AI_MARKERS_SELECTED_USE_TYPE_ID}`
				);
				let useTypeIdToSet = this.defaultUseTypeId;

				if (stored) {
					const storedId = Number(stored);
					if (useTypes.some((u) => u.use_type_id === storedId)) {
						useTypeIdToSet = storedId;
					}
				}

				const selectedUseType = useTypes.find((u) => u.use_type_id === useTypeIdToSet);
				if (selectedUseType) {
					this.useTypeFilter$.next(useTypeIdToSet);
				}

				this.languages$.next([
					{ language_code: null, language_name: this.translate.instant('common_auto') },
					...languages
				]);

				const pre = this.data.aiTranscriptionLanguage;
				const found = pre ? languages.find((l) => l.language_code === pre) : null;
				this.selectedLanguage$.next(found || this.languages$.value[0]);
				const existingIds = this.data.llmDefinitions.map((d) => d.id);
				this.checkedIds$.next(new Set<number>(existingIds));

				this.loading$.next(false);
			}),
			map(({ raw }) => raw)
		);
	}

	private setupTeaseConfig () {
		this.toggleTeaseConfig.useRealData = false;
		this.checkboxTeaseConfig.useRealData = false;
		this.aiMarkerTeaseConfig.useRealData = false;
		this.aiMarkerTeaseConfig.secondaryLink = NgxLicenseUpgradeService.getLearnMoreLink();

		if (this.featureFlagService.isAvailable('LICENSE_UPGRADE_PURCHASE')) {
			const license = this.selectedService.getLicense();
			if (this.courseTrialService.licenseTrialBlocked(this.selectedService.getOrg().group_id)) {
				// Only set upgrade action if license exists
				// When license is null (Student Pay), we hide the button and only show the learn more link
				// This is because the dialog can be opened from the library where we can't determine
				// if no license means Student Pay or just library context
				if (license) {
					this.aiMarkerTeaseConfig.promptAction =
						this.ngxLicenseUpgradeService.createTeaseWallUpgradePromptAction(license);
					this.aiMarkerTeaseConfig.licenseId = license.id;
					this.aiMarkerTeaseConfig.translationKey = 'common_upgrade';
					this.aiMarkerTeaseConfig.rememberKey = TEASE_WALL_UPGRADE_REMEMBER_KEY;
					this.ngxLicenseUpgradeService.payformOpened$
						.pipe(
							takeUntil(this.destroy$),
							filter((opened) => opened)
						)
						.subscribe(() => {
							// we need to close this modal otherwise it shows over the payment panel
							this.cancel();
						});
				}
			} else {
				this.aiMarkerTeaseConfig.titleTranslationKey = 'tease-wall_see-tools-in-action';
				this.aiMarkerTeaseConfig.translationKey = 'tease-wall_start-trial-button';
				this.aiMarkerTeaseConfig.secondaryLink = null;
				this.aiMarkerTeaseConfig.promptAction = this.courseTrialService
					.createTeaseWallCourseTrialPromptAction({
						org_id: this.selectedService.getOrg().group_id
					})
					.pipe(
						filter((result) => !!result),
						tap((result: CourseFreeTrialResult) => {
							const startedTrialOnCurrentlySelectedFolder =
								result.selectedCourse.folderId === this.selectedService.getGroup()?.group_id;

							if (startedTrialOnCurrentlySelectedFolder) {
								this.selectedService.setLicense(result.license);
								this.shouldTease = false;
								this.dataSource$.next(DataSourceType.REAL);
							} else {
								// switch to another tease wall
								this.aiMarkerTeaseConfig.titleTranslationKey = null;
								this.shouldTease = true;
								this.dataSource$.next(DataSourceType.TEASED);
							}
						})
					);
			}
		} else {
			// Remove this when cleaning up LICENSE_UPGRADE_PURCHASE
			this.aiMarkerTeaseConfig.promptAction = this.requestBetaAction();
		}
	}

	private setupRealConfig () {
		this.toggleTeaseConfig.useRealData = true;
		this.checkboxTeaseConfig.useRealData = true;
		this.aiMarkerTeaseConfig.useRealData = true;
	}

	private requestBetaAction () {
		const group = this.selectedService.getGroup();

		if (!group) return null;

		return this.selfPayService.requestBeta(group.group_id).pipe(
			map(() => {
				// On success, need to map response to translation key for tease wall
				return 'tease-wall_request-sent-message';
			}),
			catchError(() => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'tease-wall_request-failed-message'
				});
				return EMPTY;
			})
		);
	}
}
